// @ts-strict-ignore
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector, useStore } from 'react-redux';

import user, { State as UserState } from './reducers/user';
import classReducer, { State as ClassState } from './reducers/class';
import adminReducer, { State as AdminState } from './reducers/admin';
import adminAnalyticsReducer, { State as AdminAnalyticsState } from './reducers/adminAnalytics';
import module, { State as ModuleState } from './reducers/module';
import { accountManagementReducer, AccountManagementState } from './reducers/accountManagement';

export interface APIState<T> {
  data: T;
  hasError: boolean;
  errorMessage: string | null;
  fetching: boolean;
}

export interface DefaultRootState {
  user: UserState;
  class: ClassState;
  admin: AdminState;
  adminAnalytics: AdminAnalyticsState;
  module: ModuleState;
  accountManagement: AccountManagementState;
}

const reducers = {
  user,
  module,
  class: classReducer,
  admin: adminReducer,
  adminAnalytics: adminAnalyticsReducer,
  accountManagement: accountManagementReducer,
};

const allReducers = combineReducers(reducers);

const rootReducer = (state, action) => {
  if (action.type === 'users/signout/fulfilled') {
    state = undefined;
  }
  return allReducers(state, action);
};

// We don't need to explicitly add thunkMiddleware
// https://redux.js.org/tutorials/fundamentals/part-8-modern-redux#using-configurestore
export const store = configureStore<DefaultRootState>({
  reducer: rootReducer,
});

/*
 * Redux store and hook types
 */
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppStore = typeof store;

// Redux-specific hooks
// Reference https://react-redux.js.org/using-react-redux/usage-with-typescript
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
export const useAppStore = useStore.withTypes<AppStore>();
